import type { ComponentProps } from 'react';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@repo/lib/cn';

export function Popover(popoverProps: ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...popoverProps} />;
}

export function PopoverTrigger(popoverTriggerProps: ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...popoverTriggerProps} />;
}

export function PopoverContent(
  popoverContentProps: ComponentProps<typeof PopoverPrimitive.Content> & {
    disablePortal?: boolean;
  }
) {
  const { className, align = 'center', sideOffset = 4, disablePortal = false, ...props } = popoverContentProps;

  const content = (
    <PopoverPrimitive.Content
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'border bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 flex flex-col gap-2.5 rounded-lg p-2.5 text-sm shadow-md duration-150 z-50 w-72 origin-(--radix-popover-content-transform-origin) outline-0',

        className
      )}
      {...props}
    />
  );

  if (disablePortal) {
    return content;
  }

  return <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>;
}

export function PopoverAnchor(popoverAnchorProps: ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...popoverAnchorProps} />;
}

export function PopoverHeader(popoverHeaderProps: ComponentProps<'div'>) {
  const { className, ...props } = popoverHeaderProps;
  return <div data-slot="popover-header" className={cn('flex flex-col gap-0.5 text-sm', className)} {...props} />;
}

export function PopoverTitle(popoverTitleProps: ComponentProps<'h2'>) {
  const { className, ...props } = popoverTitleProps;
  return <div data-slot="popover-title" className={cn('font-medium', className)} {...props} />;
}

export function PopoverDescription(popoverDescriptionProps: ComponentProps<'p'>) {
  const { className, ...props } = popoverDescriptionProps;
  return <p data-slot="popover-description" className={cn('text-muted-foreground text-sm', className)} {...props} />;
}
